import { useEffect, useState } from 'react';
import { getFrontendEnvVariables } from '@mls-wallet/env';
import {
  isRestrictedRegionResponse,
  subscribeToRestrictedRegionDetected,
} from '@/lib/compliance/restricted-region';

const env = getFrontendEnvVariables();

function getSessionCheckUrl(): string {
  return new URL('/api/auth/get-session', env.DEN_API_URL).toString();
}

/**
 * Applies the app's startup compliance gate by probing auth access and listening
 * for later restricted-region detections from API calls.
 */
export function useRestrictedRegionGate() {
  const [isChecking, setIsChecking] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    // Case: demo mode bypasses backend compliance gating so local demos still render.
    if (env.ENVIRONMENT === 'DEMO') {
      setIsChecking(false);
      return;
    }

    const abortController = new AbortController();
    let didUnmount = false;
    let didTimeout = false;
    const timeoutId = window.setTimeout(() => {
      didTimeout = true;
      abortController.abort();
    }, 8_000);
    const unsubscribe = subscribeToRestrictedRegionDetected(() => {
      // Case: a later API request detected a restricted region and should force the blocked state.
      setIsBlocked(true);
    });

    async function checkRestrictedRegion() {
      try {
        const response = await fetch(getSessionCheckUrl(), {
          credentials: 'include',
          signal: abortController.signal,
        });

        if (response.status === 403) {
          const body = await response.json().catch(() => null);
          if (isRestrictedRegionResponse(body)) {
            // Case: the bootstrap auth probe was explicitly denied due to region restrictions.
            setIsBlocked(true);
            setIsChecking(false);
            return;
          }
        }
      } catch (_error) {
        if (didUnmount || (abortController.signal.aborted && !didTimeout)) {
          // Case: ignore aborts caused by teardown so unmount does not race into state updates.
          return;
        }
      } finally {
        window.clearTimeout(timeoutId);
      }

      // Case: bootstrap completed without a restricted-region decision, so release the loading gate.
      setIsChecking(false);
    }

    void checkRestrictedRegion();

    return () => {
      didUnmount = true;
      window.clearTimeout(timeoutId);
      abortController.abort();
      unsubscribe();
    };
  }, []);

  return { isBlocked, isChecking };
}
